import { useCallback, useEffect, useRef, useState } from 'react'
import Modal from './Modal'
import { api, ApiClientError } from '@/lib/api'
import { toastError, toastSuccess } from '@/store/toast'
import { checkAgentHealth, pollCard, type CardData } from '@/lib/cardAgent'
import type { Customer, Patient } from '@yui-receipt/shared'

type Props = {
  open: boolean
  onClose: () => void
  onCustomer: (c: Customer, patient?: Patient | null) => void
}

type Status = 'idle' | 'checking' | 'waiting' | 'review' | 'saving' | 'error' | 'not-installed'

function detectOs(): 'mac' | 'win' | 'other' {
  const ua = navigator.userAgent
  if (/Mac|iPhone|iPad/i.test(ua)) return 'mac'
  if (/Win/i.test(ua)) return 'win'
  return 'other'
}

const INSTALL_CMD = {
  mac: 'curl -fsSL https://app.workphysicaltherapy.org/api/install/card-agent-mac.sh | bash',
  win: 'iwr https://app.workphysicaltherapy.org/api/install/card-agent-win.ps1 -UseBasicParsing | iex',
}

const RESTART_CMD = {
  mac: 'curl -fsSL https://app.workphysicaltherapy.org/api/install/restart-agent-mac.sh | bash',
  win: '/api/install/restart-agent-win.cmd',
}

export default function CardScanModal({ open, onClose, onCustomer }: Props) {
  const [status, setStatus] = useState<Status>('idle')
  const [message, setMessage] = useState<string>('')
  const [osTab, setOsTab] = useState<'mac' | 'win'>(() => {
    const os = detectOs()
    return os === 'win' ? 'win' : 'mac'
  })
  const [cardData, setCardData] = useState<CardData | null>(null)
  const [hn, setHn] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [suggestedHn, setSuggestedHn] = useState('')
  const hnRef = useRef<HTMLInputElement>(null)

  const runScan = useCallback(
    (controller: AbortController) => {
      let cancelled = false
      controller.signal.addEventListener('abort', () => {
        cancelled = true
      })
      async function run() {
        setStatus('checking')
        setMessage('กำลังเชื่อมต่อ card-agent…')

        const healthy = await checkAgentHealth(controller.signal)
        if (cancelled) return
        if (!healthy) {
          setStatus('not-installed')
          setMessage('')
          return
        }

        setStatus('waiting')
        setMessage('วางบัตรประชาชนบนเครื่องอ่าน…')
        let card: CardData
        try {
          card = await pollCard({ signal: controller.signal })
        } catch (err) {
          if (cancelled) return
          const msg = err instanceof Error ? err.message : 'ไม่สามารถอ่านบัตรได้'
          if (msg === 'AGENT_TIMEOUT') {
            setStatus('error')
            setMessage('รออ่านบัตรนานเกินไป — ลองใหม่อีกครั้ง')
          } else {
            setStatus('error')
            setMessage('อ่านบัตรไม่สำเร็จ — ' + msg)
          }
          return
        }
        if (cancelled) return

        // Fetch suggested next HN
        setCardData(card)
        try {
          const nextHn = await api<{ suggestedHn: string }>('/patients/next-hn')
          if (!cancelled) {
            setSuggestedHn(nextHn.suggestedHn)
            setHn(nextHn.suggestedHn)
          }
        } catch {
          // If next-hn fails, still show review — staff types manually
          setSuggestedHn('')
          setHn('')
        }
        if (!cancelled) {
          setStatus('review')
          setTimeout(() => hnRef.current?.select(), 100)
        }
      }
      void run()
    },
    [],
  )

  async function handleConfirm() {
    if (!cardData) return
    setStatus('saving')
    setMessage('กำลังบันทึกข้อมูลคนไข้…')
    try {
      const nameTh = [cardData.titleTh, cardData.firstNameTh, cardData.lastNameTh]
        .filter(Boolean)
        .join(' ')
        .trim()
      const nameEn = [cardData.titleEn, cardData.firstNameEn, cardData.lastNameEn]
        .filter(Boolean)
        .join(' ')
        .trim()
      const res = await api<{ customer: Customer; patient: Patient | null; hnConflict?: boolean }>(
        '/customers/upsert',
        {
          body: {
            cid: cardData.cid,
            nameTh,
            nameEn: nameEn || null,
            dob: cardData.dob ?? null,
            gender: cardData.gender ?? null,
            address: cardData.address ?? null,
            phone: phone.trim() || null,
            email: email.trim() || null,
            hn: hn.trim() || null,
          },
        },
      )

      // Per PDPA, do not keep card.cid in state after upsert.
      setCardData(null)

      if (res.hnConflict) {
        toastError(`HN "${hn}" ซ้ำกับคนไข้ ${res.patient?.nameTh ?? '-'} — กรุณาเปลี่ยน HN`)
        setStatus('review')
        setHn('')
        return
      }

      const patientInfo = res.patient
      if (patientInfo) {
        toastSuccess(`เพิ่มคนไข้ HN ${patientInfo.hn} — ${nameTh}`)
      } else {
        toastSuccess(`เพิ่มคนไข้ ${nameTh}`)
      }
      onCustomer(res.customer, patientInfo)
      onClose()
    } catch (err) {
      const msg = err instanceof ApiClientError ? err.message : 'บันทึกไม่สำเร็จ'
      setStatus('error')
      setMessage(msg)
      toastError(msg)
    }
  }

  useEffect(() => {
    if (!open) return
    setCardData(null)
    setHn('')
    setPhone('')
    setEmail('')
    setSuggestedHn('')
    const controller = new AbortController()
    runScan(controller)
    return () => controller.abort()
  }, [open, runScan])

  function retry() {
    setCardData(null)
    setHn('')
    setPhone('')
    setEmail('')
    setSuggestedHn('')
    const controller = new AbortController()
    runScan(controller)
  }

  async function copyCmd() {
    try {
      await navigator.clipboard.writeText(INSTALL_CMD[osTab])
      toastSuccess('คัดลอกคำสั่งแล้ว — ไปวางใน Terminal / PowerShell')
    } catch {
      toastError('คัดลอกไม่สำเร็จ — กดเลือกและ Ctrl/Cmd+C เอง')
    }
  }

  async function restartAgent() {
    if (osTab === 'win') {
      window.location.href = RESTART_CMD.win
      toastSuccess('ดาวน์โหลดแล้ว — เปิดไฟล์ restart-card-agent.cmd เพื่อสั่งเปิดเครื่องอ่าน')
    } else {
      try {
        await navigator.clipboard.writeText(RESTART_CMD.mac)
        toastSuccess('คัดลอกคำสั่ง restart แล้ว — วางใน Terminal แล้วกด Enter')
      } catch {
        toastError('คัดลอกไม่สำเร็จ — กดเลือกและ Cmd+C เอง')
      }
    }
  }

  // Build display name from card data
  const cardNameTh = cardData
    ? [cardData.titleTh, cardData.firstNameTh, cardData.lastNameTh].filter(Boolean).join(' ').trim()
    : ''

  return (
    <Modal open={open} onClose={onClose} title="สแกนบัตรประชาชน">
      {status === 'not-installed' ? (
        <div className="space-y-4">
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
            เชื่อมต่อ <strong>card-agent</strong> ไม่ได้ — ถ้าเคยติดตั้งแล้ว กดปุ่มสีเขียวด้านล่างเพื่อสั่งเปิดใหม่
            ถ้ายังไม่เคยติดตั้ง ใช้คำสั่งล่างสุด
          </div>

          <div className="flex gap-2">
            <button
              className={
                'px-3 py-1.5 rounded-md text-sm font-medium transition ' +
                (osTab === 'mac'
                  ? 'bg-brand-600 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200')
              }
              onClick={() => setOsTab('mac')}
            >
              🍎 macOS
            </button>
            <button
              className={
                'px-3 py-1.5 rounded-md text-sm font-medium transition ' +
                (osTab === 'win'
                  ? 'bg-brand-600 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200')
              }
              onClick={() => setOsTab('win')}
            >
              🪟 Windows
            </button>
          </div>

          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 space-y-2">
            <div className="text-sm font-medium text-emerald-900">
              🔄 เคยติดตั้งแล้วแต่หยุดทำงาน?
            </div>
            <div className="text-xs text-emerald-800">
              {osTab === 'win'
                ? 'กดปุ่มด้านล่างเพื่อดาวน์โหลดตัวสั่งเปิดเครื่องอ่าน — double-click ไฟล์ที่ดาวน์โหลด → รอ 3 วิ → กด "ลองใหม่"'
                : 'กดปุ่มด้านล่างเพื่อคัดลอกคำสั่ง restart → ไปวางใน Terminal → กด Enter → กลับมากด "ลองใหม่"'}
            </div>
            <button
              className="w-full px-3 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium transition"
              onClick={restartAgent}
            >
              {osTab === 'win'
                ? '📥 ดาวน์โหลดตัวเปิดเครื่องอ่านบัตร'
                : '📋 คัดลอกคำสั่งเปิดเครื่องอ่านบัตร'}
            </button>
          </div>

          <div className="text-xs font-medium text-slate-500 pt-1">
            หรือถ้ายังไม่เคยติดตั้ง — ใช้คำสั่งติดตั้งด้านล่าง:
          </div>

          <div className="rounded-lg bg-slate-900 text-slate-100 p-3 font-mono text-xs break-all leading-relaxed select-all">
            {INSTALL_CMD[osTab]}
          </div>

          <div className="text-xs text-slate-500 space-y-1">
            {osTab === 'mac' ? (
              <>
                <div>1. เปิด <strong>Terminal</strong> (กด ⌘+Space → พิมพ์ Terminal)</div>
                <div>2. Paste คำสั่ง → กด Enter → ใส่ password ถ้าถาม</div>
                <div>3. รอสักครู่จนเห็น ✅ card-agent is running</div>
                <div>4. กลับมากดปุ่ม "ลองใหม่" ด้านล่าง</div>
              </>
            ) : (
              <>
                <div>1. เปิด <strong>Windows PowerShell</strong> (Start → พิมพ์ PowerShell)</div>
                <div>2. Paste คำสั่ง → กด Enter</div>
                <div>3. รอสักครู่จนเห็น ✅ card-agent is running</div>
                <div>4. กลับมากดปุ่ม "ลองใหม่" ด้านล่าง</div>
              </>
            )}
          </div>

          <div className="flex gap-2">
            <button className="btn-primary flex-1" onClick={copyCmd}>
              📋 คัดลอกคำสั่ง
            </button>
            <button className="btn-secondary flex-1" onClick={retry}>
              🔄 ลองใหม่
            </button>
          </div>
          <button className="btn-ghost w-full text-sm" onClick={onClose}>
            ปิด
          </button>
        </div>
      ) : status === 'review' && cardData ? (
        <div className="space-y-4">
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3">
            <div className="text-sm font-medium text-emerald-900 mb-1">อ่านบัตรสำเร็จ</div>
            <div className="text-base font-semibold">{cardNameTh}</div>
            {cardData.dob && (
              <div className="text-xs text-slate-500 mt-1">วันเกิด: {cardData.dob}</div>
            )}
          </div>
          <div>
            <label className="label">
              เลข HN (Hospital Number)
            </label>
            <input
              ref={hnRef}
              className="input font-mono"
              value={hn}
              onChange={(e) => setHn(e.target.value)}
              placeholder={suggestedHn || 'HN-000001'}
            />
            {suggestedHn && (
              <p className="text-xs text-slate-500 mt-1">
                แนะนำ: {suggestedHn} — แก้ไขได้ หรือปล่อยว่างให้ระบบสร้างให้
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">เบอร์โทร</label>
              <input
                className="input"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="08x-xxx-xxxx"
              />
            </div>
            <div>
              <label className="label">อีเมล</label>
              <input
                className="input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="example@email.com"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button className="btn-primary flex-1" onClick={handleConfirm}>
              ยืนยัน + บันทึก
            </button>
            <button className="btn-ghost flex-1" onClick={onClose}>
              ยกเลิก
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div
              className={
                'w-10 h-10 rounded-full flex items-center justify-center text-2xl ' +
                (status === 'error'
                  ? 'bg-rose-100 text-rose-600'
                  : status === 'saving'
                    ? 'bg-emerald-100 text-emerald-600'
                    : 'bg-brand-100 text-brand-600 animate-pulse')
              }
            >
              {status === 'error' ? '✕' : status === 'saving' ? '✓' : '🪪'}
            </div>
            <div className="text-slate-700">{message}</div>
          </div>
          {status === 'error' && (
            <div className="flex gap-2">
              <button className="btn-primary flex-1" onClick={retry}>
                ลองใหม่
              </button>
              <button className="btn-secondary flex-1" onClick={onClose}>
                ปิด
              </button>
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}
